import { useRef } from 'react'
import { Dialog } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { CubeIcon, ViewGridIcon } from '@heroicons/react/outline'
import { DialogView } from '../../components/DialogView'
import { formatLastPrinted, kindLabel, originText } from './historyTable'

const EMPTY = '—'
const MUTED = '#6b7280'

// Dialog values from the design's "Generar lote de etiquetas" modal; the cards copy the handheld's
// "Últimos productos recibidos" card. DialogView's card already gives sm:p-6 (24px) and shadow-xl.
const STYLES = {
  content: { width: 452, maxWidth: '100%' },
  title: { fontSize: 17, fontWeight: 600, color: '#07091A', margin: '0 0 6px' },
  subtitle: { fontSize: 13, color: '#5A608A', lineHeight: 1.5, margin: '0 0 20px' },
  list: { gap: 8, marginBottom: 24 },
  card: { gap: 10, padding: '10px 12px', borderRadius: 14, border: '1px solid #E6E8F2', background: '#fff' },
  icon: { width: 32, height: 32, borderRadius: 9 },
  iconGlyph: { width: 18, height: 18 },
  lines: { gap: 1 },
  head: { gap: 6 },
  code: { fontSize: 15, fontWeight: 700, color: '#07091A' },
  chip: { fontSize: 11, fontWeight: 600, color: '#4b5563', background: '#f3f4f6', borderRadius: 5, padding: '1px 6px' },
  muted: { fontSize: 12, color: MUTED },
  lot: { fontSize: 12, color: '#7e22ce', marginTop: 2 },
  data: { fontSize: 12.5, color: '#0F1330' },
  strong: { fontWeight: 700 },
  footer: { gap: 8 },
  cancel: { padding: '9px 16px', border: '1px solid #D8DAE8', borderRadius: 12, background: '#fff', color: '#0F1330', fontSize: 13.5, fontWeight: 600 },
  print: { padding: '6px 14px', border: 'none', borderRadius: 12, background: '#1F39FF', color: '#fff', fontSize: 13.5, fontWeight: 600 },
}

const KIND_ICONS = {
  box: { Icon: CubeIcon, background: '#dbeafe', color: '#1e40af' },
  pallet: { Icon: ViewGridIcon, background: '#f3e8ff', color: '#6b21a8' },
}
const OTHER_KIND_ICON = { Icon: CubeIcon, background: '#f3f4f6', color: '#4b5563' }

const present = value => value !== null && value !== undefined && value !== ''

// 'YYYY-MM-DD' → 'DD/MM/YYYY' as plain text, so no time zone can move the day.
const formatDay = value => {
  const [, year, month, day] = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '') || []
  return year ? `${day}/${month}/${year}` : null
}

const joinParts = parts => parts.filter(present).join(' · ')

const LabelledLine = ({ label, children }) => (
  <div style={STYLES.data}>
    {label} {children}
  </div>
)

const CandidateCard = ({ candidate, timeZone, onChoose }) => {
  const { t } = useTranslation()
  const { Icon, background, color } = KIND_ICONS[candidate.kind] || OTHER_KIND_ICON
  const kind = present(candidate.kind) ? kindLabel(candidate.kind, t) : null
  const label = candidate.kind === 'box' && candidate.label ? candidate.label : {}
  const product = joinParts([
    label.product_name,
    present(label.pieces_per_box) ? t('pending_prints.ambiguous.pieces_per_box', { count: label.pieces_per_box }) : null,
  ])
  const expires = formatDay(label.expiration_date)
  const origin = originText(candidate.origin_type, candidate.origin_id, t)
  const created = formatLastPrinted(candidate.created_at, timeZone)
  const hasPrintCode = present(candidate.print_code) && candidate.expired !== true

  return (
    <li className="flex items-start flex-wrap" style={STYLES.card}>
      <div data-kind-icon={candidate.kind || 'other'} className="flex flex-shrink-0 items-center justify-center" style={{ ...STYLES.icon, background, color }}>
        <Icon style={STYLES.iconGlyph} aria-hidden="true" />
      </div>
      <div className="flex flex-col flex-1 min-w-0" style={STYLES.lines}>
        {(present(candidate.barcode) || kind) && (
          <div className="flex items-center flex-wrap" style={STYLES.head}>
            {present(candidate.barcode) && (
              <span className="font-mono break-all" style={STYLES.code}>
                {candidate.barcode}
              </span>
            )}
            {kind && <span style={STYLES.chip}>{kind}</span>}
          </div>
        )}
        {product && <div style={STYLES.muted}>{product}</div>}
        {present(label.sku) && <div style={STYLES.muted}>{t('pending_prints.ambiguous.sku', { sku: label.sku })}</div>}
        {present(label.lot_code) && (
          <div style={STYLES.lot}>
            <strong>{t('pending_prints.ambiguous.lot', { lot: label.lot_code })}</strong>
            {expires && ` · ${t('pending_prints.ambiguous.lot_expires', { date: expires })}`}
          </div>
        )}
        {present(candidate.store_name) && (
          <LabelledLine label={t('pending_prints.ambiguous.store')}>
            <span style={STYLES.strong}>{candidate.store_name}</span>
          </LabelledLine>
        )}
        {origin && <LabelledLine label={t('pending_prints.ambiguous.origin')}>{origin}</LabelledLine>}
        {created !== EMPTY && (
          <LabelledLine label={t('pending_prints.ambiguous.created')}>
            <span>{created}</span>
          </LabelledLine>
        )}
        {hasPrintCode ? (
          <LabelledLine label={t('pending_prints.ambiguous.print_code')}>
            <span className="font-mono" style={STYLES.strong}>
              {candidate.print_code}
            </span>
          </LabelledLine>
        ) : (
          // An expired job: choosing it asks the backend to re-issue it.
          <div style={STYLES.data}>{t('pending_prints.ambiguous.new_code')}</div>
        )}
      </div>
      <button type="button" className="flex-shrink-0 ml-auto" style={STYLES.print} onClick={() => onChoose(candidate)}>
        {t('pending_prints.ambiguous.print')}
      </button>
    </li>
  )
}

export const StoreChoiceDialog = ({ open, code, candidates, timeZone, onChoose, onCancel, afterLeave }) => {
  const { t } = useTranslation()
  // Focus the title, not the first store's button: a scanner's trailing Enter must not choose a store.
  const titleRef = useRef(null)

  return (
    <DialogView open={open} setOpen={onCancel} afterLeave={afterLeave} initialFocus={titleRef} className=" rounded-2xl">
      <div style={STYLES.content}>
        <Dialog.Title as="h2" ref={titleRef} tabIndex={-1} className="focus:outline-none" style={STYLES.title}>
          {t('pending_prints.ambiguous.title')}
        </Dialog.Title>
        <p style={STYLES.subtitle}>{t('pending_prints.ambiguous.subtitle', { code })}</p>
        <ul className="flex flex-col" style={STYLES.list}>
          {(candidates || []).map(candidate => (
            // One card per candidate job; two can share a store.
            <CandidateCard key={candidate.id ?? candidate.store_id} candidate={candidate} timeZone={timeZone} onChoose={onChoose} />
          ))}
        </ul>
        <div className="flex justify-end" style={STYLES.footer}>
          <button type="button" style={STYLES.cancel} onClick={onCancel}>
            {t('pending_prints.ambiguous.cancel')}
          </button>
        </div>
      </div>
    </DialogView>
  )
}
